import React from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';

import type { LoadMainVariantProps } from '../types';
import { useStaggeredWave } from '../loadMainMotion';

function Dot({
  size,
  color,
  glow_color,
  duration_ms,
  index,
  count,
  is_rtl,
}: {
  size: number;
  color: string;
  glow_color: string;
  duration_ms: number;
  index: number;
  count: number;
  is_rtl: boolean;
}) {
  const phase = useStaggeredWave(duration_ms, index, count, is_rtl);

  const dot_style = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(phase.value, [0, 1], [0, -size * 0.42]) },
      { scale: interpolate(phase.value, [0, 1], [0.72, 1]) },
    ],
    opacity: interpolate(phase.value, [0, 1], [0.38, 1]),
  }));

  const halo_style = useAnimatedStyle(() => ({
    opacity: interpolate(phase.value, [0, 1], [0, 0.55]),
    transform: [{ scale: interpolate(phase.value, [0, 1], [0.8, 1.45]) }],
  }));

  return (
    <View style={{ width: size * 1.1, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Animated.View
        style={[
          {
            position: 'absolute',
            width: size * 1.2,
            height: size * 1.2,
            borderRadius: size,
            backgroundColor: glow_color,
          },
          halo_style,
        ]}
      />
      <Animated.View
        style={[
          {
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: color,
          },
          dot_style,
        ]}
      />
    </View>
  );
}

export function DotsLoader({ size, colors, duration_ms, is_rtl }: LoadMainVariantProps) {
  const dot_size = Math.max(5, Math.round(size * 0.15));
  const gap = Math.max(3, Math.round(size * 0.06));
  const count = 3;

  return (
    <View
      style={[
        styles.row,
        {
          width: size,
          height: size,
          gap,
          flexDirection: 'row',
        },
      ]}>
      {Array.from({ length: count }, (_, index) => (
        <Dot
          key={index}
          size={dot_size}
          color={colors.primary}
          glow_color={colors.glow}
          duration_ms={duration_ms}
          index={index}
          count={count}
          is_rtl={is_rtl}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
